
import React, { useEffect } from 'react';
import MainSidebar from './MainSidebar';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { resolveActiveSiteCode } from '../utils/navigationContext';

interface DashboardLayoutProps {
    children: React.ReactNode;
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { site } = useParams<{ site: string }>();
    const { user, isGlobalAdmin } = useAuth();

    // Auto-redirect if non-admin user is on a wrong site URL (Allow HO Admin & System Admin multi-site access)
    useEffect(() => {
        const rawRole = String(user?.roleName || user?.role || '').trim().toLowerCase().replace(' ', '_');
        const canAccessMultipleSites = isGlobalAdmin || ['ho_admin', 'company_admin', 'ho', 'system_admin', 'super_admin', 'admin'].includes(rawRole);

        if (user && !canAccessMultipleSites) {
            const userSite = resolveActiveSiteCode({
                userSiteCode: user.siteCode,
                userTenant: user.tenant,
                preferStoredContext: true
            });
            const urlSite = site?.toUpperCase();

            // Only redirect if a site param exists and doesn't match the user's assigned site
            if (urlSite && userSite && urlSite !== userSite) {
                // Construct the correct path by replacing the site code in the URL
                // Most routes are /:page/:site/...
                const currentPath = location.pathname;
                const pathParts = currentPath.split('/');

                // Find where the site code is in the path and replace it
                // Most routes are /:page/:site/...
                const newPath = currentPath.replace(`/${site}`, `/${userSite}`);

                if (newPath !== currentPath) {
                    navigate(newPath, { replace: true });
                }
            }
        }
    }, [user, isGlobalAdmin, site, location.pathname, navigate]);

    const isNavigatePage = location.pathname.includes('/navigate/');

    const marginLeft = isNavigatePage ? '18rem' : '4rem';

    return (
        <div className="flex min-h-screen">
            <MainSidebar />
            <div
                className="flex-1 transition-all duration-300 min-w-0"
                style={{
                    marginLeft,
                    width: `calc(100% - ${marginLeft})`,
                    maxWidth: `calc(100% - ${marginLeft})`,
                }}
            >
                {children}
            </div>
        </div>
    );
};

export default DashboardLayout;

